<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background using login.png -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style="background-image: url('/login.png')"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>
    
    <!-- Content Container -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-32 lg:px-8">
      <div class="bg-white rounded-2xl max-w-md w-full p-6 border-4 border-black shadow-[8px_8px_0_rgba(0,0,0,1)]">
        <!-- Header -->
      <div class="text-center mb-6">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
        <p class="text-sm text-gray-600">
          Enter the 6-digit code sent to your email address
        </p>
        <p class="text-xs text-gray-500 mt-2" v-if="expiresAt">
          Code expires in: <span class="font-mono font-semibold">{{ timeRemaining }}</span>
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-center">
          <svg class="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm text-green-700">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex items-center">
          <svg class="h-5 w-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm text-red-700">{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Code Input -->
      <div class="mb-6">
        <div class="flex justify-center space-x-2">
          <input
            v-for="(digit, index) in codeDigits"
            :key="index"
            :ref="el => (inputRefs[index] = el as HTMLInputElement)"
            v-model="codeDigits[index]"
            type="text"
            inputmode="numeric"
            maxlength="1"
            class="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
            :class="{
              'border-red-500 focus:border-red-500 focus:ring-red-200': errorMessage,
              'border-green-500 focus:border-green-500 focus:ring-green-200': successMessage
            }"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @paste="handlePaste"
            :disabled="isVerifying || successMessage !== ''"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-col space-y-3">
        <button
          @click="handleVerify"
          :disabled="!isCodeComplete || isVerifying"
          class="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <span v-if="!isVerifying">Verify Code</span>
          <span v-else class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Verifying...
          </span>
        </button>

        <button
          @click="handleResend"
          :disabled="isResending || cannotResend"
          class="w-full px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <span v-if="!isResending">{{ resendButtonText }}</span>
          <span v-else>Sending...</span>
        </button>

        <button
          @click="handleCancel"
          :disabled="isVerifying"
          class="w-full px-4 py-2 text-gray-600 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
      </div>

      <!-- Security Notice -->
      <div class="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div class="flex">
          <svg class="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <p class="text-xs text-yellow-700">
            If you didn't attempt to log in, please close this window and contact support.
          </p>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { twoFactorAuthService } from '@/services/twoFactorAuthService';
import { logDebug, logInfo, logWarn, logError, logSuccess } from '@/utils/secureLogger';

const router = useRouter();
const route = useRoute();

// Props from route params
const twoFAId = ref(route.params.twoFAId as string);
const expiresAt = ref(route.query.expiresAt as string);

// State
const codeDigits = ref<string[]>(['', '', '', '', '', '']);
const inputRefs = ref<(HTMLInputElement | null)[]>([]);
const isVerifying = ref(false);
const isResending = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const timeRemaining = ref('');
const resendCooldown = ref(0);
let timerInterval: number | null = null;
let resendInterval: number | null = null;

// Computed
const isCodeComplete = computed(() => {
  return codeDigits.value.every(digit => digit !== '');
});

const verificationCode = computed(() => {
  return codeDigits.value.join('');
});

const cannotResend = computed(() => {
  return resendCooldown.value > 0;
});

const resendButtonText = computed(() => {
  if (resendCooldown.value > 0) {
    return `Resend in ${resendCooldown.value}s`;
  }
  return 'Resend Code';
});

// Timer functions
const startTimer = () => {
  if (!expiresAt.value) return;
  
  // Clear any existing timer first to prevent multiple timers running
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  
  const updateTimer = () => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt.value).getTime();
    const diff = expiry - now;
    
    if (diff <= 0) {
      timeRemaining.value = 'Expired';
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
      return;
    }
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    timeRemaining.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  updateTimer();
  timerInterval = setInterval(updateTimer, 1000);
};

const startResendCooldown = () => {
  // Clear any existing cooldown timer first
  if (resendInterval) {
    clearInterval(resendInterval);
    resendInterval = null;
  }
  
  resendCooldown.value = 30;
  resendInterval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      if (resendInterval) {
        clearInterval(resendInterval);
        resendInterval = null;
      }
    }
  }, 1000);
};

// Input handling
const handleInput = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  
  if (value && !/^\d$/.test(value)) {
    target.value = '';
    return;
  }
  
  codeDigits.value[index] = value;
  
  if (value && index < 5) {
    inputRefs.value[index + 1]?.focus();
  }
  
  // Clear error when user starts typing
  if (errorMessage.value) {
    errorMessage.value = '';
  }
};

const handleKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
    inputRefs.value[index - 1]?.focus();
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text') || '';
  const digits = pastedData.replace(/\D/g, '').slice(0, 6);
  
  for (let i = 0; i < 6; i++) {
    codeDigits.value[i] = digits[i] || '';
  }
  
  // Focus the next empty input or the last one
  const nextEmptyIndex = codeDigits.value.findIndex(digit => !digit);
  const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
  inputRefs.value[focusIndex]?.focus();
};

const clearCode = () => {
  codeDigits.value = ['', '', '', '', '', ''];
  inputRefs.value[0]?.focus();
};

// Actions
const handleVerify = async () => {
  if (!isCodeComplete.value || isVerifying.value) {
    return;
  }
  
  isVerifying.value = true;
  errorMessage.value = '';
  
  try {
    logDebug('Verifying 2FA code');
    
    const result = await twoFactorAuthService.verify2FACode(
      twoFAId.value,
      verificationCode.value,
      false // Don't trust yet
    );

    if (result.verified) {
      logSuccess('2FA verification successful');
      successMessage.value = 'Verification successful!';
      
      // Navigate to trust device page
      setTimeout(() => {
        router.push({
          name: 'TrustDevice',
          query: { 
            twoFAId: twoFAId.value,
            expiresAt: expiresAt.value 
          }
        });
      }, 1000);
    } else {
      errorMessage.value = result.message || 'Verification failed';
      clearCode();
    }
  } catch (error: any) {
    logError('2FA verification error', error);
    errorMessage.value = error.message || 'Verification failed. Please try again.';
    clearCode();
  } finally {
    isVerifying.value = false;
  }
};

const handleResend = async () => {
  if (isResending.value || cannotResend.value) {
    return;
  }
  
  isResending.value = true;
  errorMessage.value = '';
  
  try {
    logDebug('Resending 2FA code');
    
    const result = await twoFactorAuthService.resend2FACode(twoFAId.value);
    
    if (result.success) {
      logSuccess('2FA code resent successfully');
      successMessage.value = 'New code sent!';
      
      // Update with new 2FA ID and expiry from the resend response
      if (result.twoFAId) {
        twoFAId.value = result.twoFAId;
        logDebug('Updated twoFAId after resend', { newId: result.twoFAId });
      }
      if (result.expiresAt) {
        expiresAt.value = result.expiresAt;
        startTimer(); // Restart the expiration timer
      }
      
      startResendCooldown();
      
      setTimeout(() => {
        successMessage.value = '';
      }, 3000);
    } else {
      errorMessage.value = result.message || 'Failed to resend code';
    }
  } catch (error: any) {
    logError('Error resending 2FA code', error);
    errorMessage.value = 'Failed to resend code. Please try again.';
  } finally {
    isResending.value = false;
  }
};

const handleCancel = async () => {
  try {
    logDebug('2FA cancelled by user');
    
    // Clear any partial 2FA verification
    twoFactorAuthService.clear2FAVerification();
    
    // Sign out the user since they cancelled 2FA
    const { auth, signOut } = await import('@/firebase');
    await signOut(auth);
    
    logInfo('User signed out due to 2FA cancellation');
    
    // Redirect to login
    router.push('/login');
  } catch (error: any) {
    logError('Error during 2FA cancellation', error);
    // Force redirect even if signout fails
    router.push('/login');
  }
};

// Lifecycle
onMounted(() => {
  if (!twoFAId.value) {
    logError('No 2FA ID provided');
    router.push('/login');
    return;
  }
  
  // Start expiration timer
  startTimer();
  
  // Focus first input
  setTimeout(() => {
    inputRefs.value[0]?.focus();
  }, 100);
});

onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
  if (resendInterval) {
    clearInterval(resendInterval);
  }
});
</script>
